import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Sparkles, Mail, LockKeyhole, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';

const DURATIONS = [2, 3, 4, 5, 6];
const LANGUAGES = ['English', 'Italian', 'Spanish', 'German', 'French', 'Portuguese'];

function cityLabel(city) {
  return city.country ? `${city.name}, ${city.country}` : city.name;
}

function uniqueCities(list) {
  const byName = new Map();
  (list || []).forEach((city) => {
    const name = city.name || city.city || '';
    if (!name) return;
    const key = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    byName.set(key, { ...city, name });
  });
  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export default function Book() {
  const { user, refresh } = useAuth();
  const googleButtonRef = useRef(null);
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [f, setF] = useState({
    city_id: '',
    custom_city_name: '',
    date: '',
    start_time: '19:00',
    duration_hours: 2,
    number_of_people: 1,
    preferred_language: 'English',
    customer_email: '',
    password: '',
    accepted_no_paranormal_guarantee: false,
    accepted_safety: false,
    accepted_terms: false,
    accepted_cancellation_policy: false,
    accepted_local_meetup: false,
  });

  useEffect(() => {
    api.get('/cities').then(r => setCities(uniqueCities(r.data || []))).catch(() => setCities([]));
  }, []);

  useEffect(() => {
    if (user?.email) setF(s => ({ ...s, customer_email: user.email }));
  }, [user]);

  const minDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const total = f.duration_hours * f.number_of_people * 50;
  const allConsents = f.accepted_no_paranormal_guarantee && f.accepted_safety && f.accepted_terms && f.accepted_cancellation_policy && f.accepted_local_meetup;

  const bookingPayload = () => ({
    city_id: f.city_id || null,
    custom_city_name: f.city_id ? '' : f.custom_city_name,
    date: f.date,
    start_time: f.start_time,
    duration_hours: f.duration_hours,
    number_of_people: f.number_of_people,
    preferred_language: f.preferred_language,
    interest_tags: [],
    special_requests: '',
    customer_name: user?.name || f.customer_email.split('@')[0] || 'Traveller',
    customer_email: user?.email || f.customer_email,
    customer_phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    female_host_preferred: false,
    add_on_guide_selected: false,
    accepted_terms: f.accepted_terms,
    accepted_safety: f.accepted_safety,
    accepted_no_paranormal_guarantee: f.accepted_no_paranormal_guarantee,
    accepted_not_guide_service: f.accepted_local_meetup,
    accepted_cancellation_policy: f.accepted_cancellation_policy,
  });

  const validateBooking = () => {
    if (!f.city_id && !f.custom_city_name.trim()) { toast.error('Pick a city or request another city.'); return false; }
    if (!f.date) { toast.error('Pick a date.'); return false; }
    if (!allConsents) { toast.error('Please accept all booking confirmations.'); return false; }
    return true;
  };

  const startCheckout = async () => {
    const r = await api.post('/bookings/checkout', bookingPayload());
    if (r.data.url) window.location.href = r.data.url;
    else if (r.data.redirect) window.location.href = r.data.redirect;
  };

  const submitWithEmail = async (e) => {
    e.preventDefault();
    setExistingAccount(false);
    if (!validateBooking()) return;
    if (!user && (!f.customer_email || !f.password)) { toast.error('Add email and password, or sign in with Google.'); return; }
    setSubmitting(true);
    try {
      if (!user) {
        try {
          await api.post('/auth/signup', {
            role: 'customer',
            email: f.customer_email,
            password: f.password,
            name: f.customer_email.split('@')[0],
          });
          await refresh();
        } catch (err) {
          if (err?.response?.status === 409) {
            setExistingAccount(true);
            setSubmitting(false);
            return;
          }
          throw err;
        }
      }
      await startCheckout();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Booking failed');
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    const initialize = () => {
      if (!googleButtonRef.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          setExistingAccount(false);
          if (!validateBooking()) return;
          setSubmitting(true);
          try {
            await api.post('/auth/google', { credential, role: 'customer' });
            await refresh();
            await startCheckout();
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Google sign in failed');
            setSubmitting(false);
          }
        },
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'filled_black',
        size: 'large',
        shape: 'pill',
        width: 320,
        text: 'continue_with',
      });
    };
    if (window.google?.accounts?.id) { initialize(); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = initialize;
    document.head.appendChild(script);
  }, [f, user, refresh]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="badge-cozy mb-3">Book a local</div>
      <h1 className="text-4xl sm:text-5xl font-heading font-semibold mb-2">Book your local 👻</h1>
      <p className="text-[#a3a3a3] mb-10">Pick your city, time, and language. We’ll match you with a local Ghostie.</p>

      <form onSubmit={submitWithEmail} className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="label">Pick a city</label>
          <select value={f.city_id} onChange={e=>setF(s=>({...s, city_id: e.target.value, custom_city_name:''}))} className="input-cozy" data-testid="book-city">
            <option value="">Pick a city...</option>
            {cities.map(c => <option key={c.city_id || c.name} value={c.city_id}>{cityLabel(c)}</option>)}
          </select>
          {!f.city_id && (
            <input className="input-cozy mt-3" placeholder="Or request a city" value={f.custom_city_name} onChange={e=>setF(s=>({...s, custom_city_name: e.target.value}))} data-testid="book-custom-city"/>
          )}
        </div>

        <div>
          <label className="label">Date</label>
          <input required type="date" min={minDate} value={f.date} onChange={e=>setF(s=>({...s, date: e.target.value}))} className="input-cozy" data-testid="book-date"/>
        </div>
        <div>
          <label className="label">Start time</label>
          <input required type="time" value={f.start_time} onChange={e=>setF(s=>({...s, start_time: e.target.value}))} className="input-cozy" data-testid="book-start-time"/>
        </div>
        <div>
          <label className="label">Duration</label>
          <select value={f.duration_hours} onChange={e=>setF(s=>({...s, duration_hours: +e.target.value}))} className="input-cozy" data-testid="book-duration">
            {DURATIONS.map(d => <option key={d} value={d}>{d} hours</option>)}
          </select>
        </div>
        <div>
          <label className="label">People</label>
          <input required type="number" min={1} max={6} value={f.number_of_people} onChange={e=>setF(s=>({...s, number_of_people: +e.target.value}))} className="input-cozy" data-testid="book-people"/>
        </div>
        <div className="md:col-span-2">
          <label className="label">Preferred language</label>
          <select value={f.preferred_language} onChange={e=>setF(s=>({...s, preferred_language: e.target.value}))} className="input-cozy" data-testid="book-language">
            {LANGUAGES.map(language => <option key={language}>{language}</option>)}
          </select>
        </div>

        <div className="md:col-span-2 rounded-3xl border border-[#3f2e1e] bg-[#111]/45 p-5 space-y-3 text-sm">
          {[
            ['accepted_no_paranormal_guarantee', 'I understand paranormal activity is not guaranteed.'],
            ['accepted_safety', 'I agree to the safety guidelines.'],
            ['accepted_terms', 'I agree to the terms, cancellation policy and paranormal disclaimer.'],
            ['accepted_cancellation_policy', 'I understand there is a 48h free cancellation policy.'],
            ['accepted_local_meetup', 'I understand this experience is a meetup with a local who shares my same folklore interests and not a traditional ghost tour or sightseeing tour.'],
          ].map(([k, label]) => (
            <label key={k} className="flex items-start gap-3 cursor-pointer">
              <input required type="checkbox" checked={f[k]} onChange={e=>setF(s=>({...s, [k]: e.target.checked}))} className="mt-1 accent-[#f97316]" data-testid={`consent-${k}`}/>
              <span>{label}</span>
            </label>
          ))}
        </div>

        <div className="md:col-span-2 rounded-3xl border border-[#3f2e1e] bg-[#111]/45 p-6">
          <div className="text-center mb-5">
            <div className="text-xl font-semibold text-[#fafafa]">Sign in with</div>
            <div className="mt-4 flex justify-center"><div ref={googleButtonRef} /></div>
          </div>

          <div className="my-6 flex items-center gap-4 text-sm font-bold text-[#a3a3a3]">
            <span className="h-px bg-[#3f2e1e] flex-1" /> Or <span className="h-px bg-[#3f2e1e] flex-1" />
          </div>

          <div className="space-y-4 max-w-xl mx-auto">
            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                <input required={!user} type="email" value={f.customer_email} onChange={e=>setF(s=>({...s, customer_email:e.target.value}))} className="input-cozy pl-12" data-testid="book-email"/>
              </div>
            </div>
            <div>
              <label className="label">Password</label>
              <div className="relative">
                <LockKeyhole size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                <input required={!user} type={showPassword ? 'text' : 'password'} minLength={8} value={f.password} onChange={e=>setF(s=>({...s, password:e.target.value}))} className="input-cozy pl-12 pr-12" data-testid="book-password"/>
                <button type="button" onClick={()=>setShowPassword(v=>!v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a3a3a3]">
                  {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>

            {existingAccount && (
              <div className="rounded-2xl border border-[#f97316]/50 bg-[#f97316]/10 p-4 text-sm text-[#f8d7bd]">
                This account already exists. Please <Link to="/login" className="text-[#f97316] underline">log in</Link> and book from your dashboard.
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="book-submit">
              <Sparkles size={16}/> {submitting ? 'Processing...' : `Pay & Book · €${total.toFixed(2)}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
